import clsx from 'clsx'
import { rem } from 'polished'
import * as React from 'react'
import { useCallback } from 'react'
import styled from 'styled-components'

import { mediaQueryUp } from 'components/utils/breakpoint'
import noop from 'lib/function/noop'

import type { CalendarDayState } from './Calendar.types'

export const LANE_HEIGHT = 19
export const LANE_GAP = 3

const HATCH = 'repeating-linear-gradient(-45deg,transparent,transparent 5px,#EDEDEC 5px,#EDEDEC 6px)'

const Cell = styled.button`
  position: relative;
  /*
   * Flex column rather than block: a button centres its content vertically by
   * default, so a day with no chips floated its number 24px below one that
   * had them, in the same row.
   */
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  min-height: ${rem(58)};
  padding: ${rem(7)} ${rem(3)} ${rem(9)};
  border: 0;
  border-radius: ${rem(8)};
  background: none;
  font: inherit;
  text-align: start;
  cursor: pointer;
  color: var(--lux-cal-text);
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(62)};
    padding: ${rem(9)} ${rem(6)} ${rem(11)};
    border-inline-end: 1px solid var(--lux-cal-border);
    border-bottom: 1px solid var(--lux-cal-border);
    border-radius: 0;
  }

  &.centred { text-align: center; }

  &:hover:not(.state-disabled):not(.adjacent) { background: rgb(0 0 0 / 4%); }

  &:focus-visible {
    outline: 2px solid var(--lux-cal-selected);
    outline-offset: -2px;
  }

  &.adjacent,
  &.past,
  &:disabled { cursor: default; }

  &.adjacent { background: transparent; }
  &.unavailable { background: var(--lux-cal-disabled-bg); }
  &.past { background-image: ${HATCH}; }

  &.state-in-range,
  &.state-preview-range { background: var(--lux-cal-range-fill); }

  &.state-range-start,
  &.state-range-end,
  &.state-preview-end,
  &.state-selected { background: var(--lux-cal-range-edge); }
`

const DayNumber = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(5)};
  font-size: ${rem(15)};
  line-height: ${rem(20)};

  .centred > & { justify-content: center; }
  .adjacent > &,
  .unavailable > &,
  .past > & { color: var(--lux-cal-faint); }

  .today > & > b {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${rem(21)};
    height: ${rem(21)};
    border-radius: 99px;
    box-shadow: inset 0 0 0 ${rem(1.5)} currentColor;
    font-size: ${rem(13)};
    font-weight: 400;
  }
`

/**
 * Reserves the rows the span bars float over. Height scales with the week's
 * lane count so overlapping stays stack instead of covering the day's content.
 */
const LaneSpacer = styled.div<{ $lanes: number }>`
  height: ${props => rem(props.$lanes * LANE_HEIGHT + LANE_GAP)};
`

interface Props extends Utils.PropsWithDataAttributes {
  date: string
  day: number
  state: CalendarDayState
  /** Padding day from the previous or next month. Renders empty. */
  isAdjacentMonth?: boolean
  isToday?: boolean
  /** Earlier than today. Hatched, and never shown when spans are in play. */
  isPast?: boolean
  /** No availability. Greyed rather than hatched. */
  isUnavailable?: boolean
  /** How many span lanes this week reserves above the content. */
  laneCount?: number
  /** Centre the content — mobile, when there is nothing overlaying the grid. */
  centred?: boolean
  disabled?: boolean
  onSelect?: (date: string) => void
  onDragOver?: React.DragEventHandler<HTMLButtonElement>
  onDrop?: React.DragEventHandler<HTMLButtonElement>
  ariaLabel?: string
  className?: string
  /** Price line, chips, badges — everything the product owns. */
  children?: React.ReactNode
  /** Rendered next to the day number, e.g. an exclusive-packages badge. */
  badge?: React.ReactNode
}

/**
 * The frame of a day: number, selection background, today marker, focus ring
 * and the space span bars float over. Everything a product wants to say about
 * a date goes in `children`, so no vertical's vocabulary reaches this file.
 */
function CalendarDayCell(props: Props) {
  const {
    date,
    day,
    state,
    isAdjacentMonth = false,
    isToday = false,
    isPast = false,
    isUnavailable = false,
    laneCount = 0,
    centred = false,
    disabled = false,
    onSelect = noop,
    onDragOver = noop,
    onDrop = noop,
    ariaLabel,
    className,
    children,
    badge,
    ...dataAttributes
  } = props

  const onClick = useCallback(() => onSelect(date), [date, onSelect])

  const classes = clsx(className, `state-${state}`, {
    adjacent: isAdjacentMonth,
    today: isToday,
    past: isPast,
    unavailable: isUnavailable,
    centred,
  })

  return <Cell
    {...dataAttributes}
    type="button"
    className={classes}
    data-calendar-date={date}
    disabled={disabled || isAdjacentMonth}
    aria-label={ariaLabel}
    aria-current={isToday ? 'date' : undefined}
    onClick={onClick}
    onDragOver={onDragOver}
    onDrop={onDrop}
  >
    <DayNumber>
      {isAdjacentMonth ? <>&nbsp;</> : <><b>{day}</b>{badge}</>}
    </DayNumber>
    {!isAdjacentMonth && <>
      {laneCount > 0 && <LaneSpacer $lanes={laneCount} />}
      {children}
    </>}
  </Cell>
}

export default CalendarDayCell
